import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loginWithType, googleAuth, API_RESOLVED_FOR_ERRORS } from '../api/api';
import { identifyBrandCreator, resetPosthog } from '../analytics/posthog';
import { getPostAuthPath } from '../config/productFlags';
import './Auth.css';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  
  // Get redirect URL and type from query params
  const redirectUrl = searchParams.get('redirect');
  const typeFromUrl = searchParams.get('type');
  const sessionExpired = searchParams.get('session_expired') === '1';
  
  const [loginType, setLoginType] = useState(typeFromUrl || 'brand'); // 'brand' or 'creator'
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const loginTypeRef = useRef(loginType);
  const redirectUrlRef = useRef(redirectUrl);
  useEffect(() => {
    loginTypeRef.current = loginType;
  }, [loginType]);
  useEffect(() => {
    redirectUrlRef.current = redirectUrl;
  }, [redirectUrl]);

  // Load remembered email on mount and set login type from URL
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
    // Set login type from URL if provided
    if (typeFromUrl === 'creator' || typeFromUrl === 'brand') {
      setLoginType(typeFromUrl);
    }
  }, [typeFromUrl]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleTypeChange = (type) => {
    setLoginType(type);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await loginWithType(formData.email, formData.password, loginType);
      const { access_token, user_id, user_type, user_name, instagram_connected } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('userEmail', formData.email);
      localStorage.setItem('userName', user_name || '');
      localStorage.setItem('userType', user_type || loginType);
      localStorage.setItem('userId', user_id || '');
      
      // Handle remember me
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      // Store Instagram connection status for creators
      if (user_type === 'creator') {
        localStorage.setItem('instagramConnected', instagram_connected ? 'true' : 'false');
      }

      resetPosthog();
      identifyBrandCreator(user_id || '', user_type || loginType);

      // Navigate to redirect URL or appropriate dashboard
      if (redirectUrl) {
        navigate(redirectUrl);
      } else {
        navigate(getPostAuthPath(user_type));
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object'
            ? JSON.stringify(detail)
            : '';

      if (status === 401) {
        setError(
          `Invalid ${loginType} credentials — wrong email/password, wrong account type (Brand vs Creator), or Google-only account. ${detailStr ? `(${detailStr})` : ''}`.trim()
        );
      } else if (status === 404) {
        setError(`No ${loginType} account found with this email. Try switching to ${loginType === 'brand' ? 'Creator' : 'Brand'} login.`);
      } else if (status === 429) {
        setError(detailStr || 'Too many login attempts. Wait a minute and try again, or restart the API server in dev.');
      } else if (status >= 500) {
        setError(
          'Server error — backend may be down or cannot reach the database (check MongoDB Atlas IP allowlist and API logs).'
        );
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError(
          `Request timed out talking to ${API_RESOLVED_FOR_ERRORS}. Is the backend running? On Render, wait for cold start and retry.`
        );
      } else if (!err.response) {
        setError(
          `Cannot reach API (${API_RESOLVED_FOR_ERRORS}). Start uvicorn (port 8000), check frontend/.env (REACT_APP_USE_DEV_PROXY for local), and check firewall/VPN.`
        );
      } else {
        setError(detailStr || 'Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth Handler (stable + refs so GSI script is not reloaded when login type changes)
  const handleGoogleLogin = useCallback(async (response) => {
    if (!response.credential && !response.access_token) {
      setError('Google authentication failed');
      return;
    }

    const token = response.credential || response.access_token;
    const lt = loginTypeRef.current;
    const redir = redirectUrlRef.current;
    setGoogleLoading(true);
    setError('');

    try {
      const res = await googleAuth(token, lt, {});
      const { access_token, user_id, user_type, user_name, email, instagram_connected } = res.data;

      // Store auth data
      localStorage.setItem('token', access_token);
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userName', user_name || '');
      localStorage.setItem('userType', user_type);
      localStorage.setItem('userId', user_id);
      
      if (user_type === 'creator') {
        localStorage.setItem('instagramConnected', instagram_connected ? 'true' : 'false');
      }

      resetPosthog();
      identifyBrandCreator(user_id, user_type);

      // Navigate to redirect URL or appropriate dashboard
      if (redir) {
        navigate(redir);
      } else {
        navigate(getPostAuthPath(user_type));
      }
    } catch (err) {
      if (err.response?.data?.detail === 'new_user_brand') {
        // User doesn't exist, redirect to signup
        setError('No account found. Please sign up first.');
        setTimeout(() => navigate('/signup'), 2000);
      } else {
        setError(err.response?.data?.detail || 'Google login failed');
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate]);

  const googleLoginCbRef = useRef(handleGoogleLogin);
  googleLoginCbRef.current = handleGoogleLogin;

  const googleTokenClientRef = useRef(null);

  // OAuth token client — reliable for a custom "Google" button. One Tap (`id.prompt()`) often
  // does nothing when tied to a custom click handler, especially on localhost.
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn('Google Client ID not configured');
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google?.accounts?.oauth2?.initTokenClient) {
        console.warn('Google OAuth2 token client unavailable');
        return;
      }
      googleTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: (tokenResponse) => {
          if (tokenResponse.error) {
            setGoogleLoading(false);
            const quiet = ['popup_closed_by_user', 'access_denied', 'user_cancelled'].includes(
              String(tokenResponse.error)
            );
            if (!quiet) {
              setError(
                tokenResponse.error_description ||
                  tokenResponse.error ||
                  'Google sign-in failed'
              );
            }
            return;
          }
          if (tokenResponse.access_token) {
            googleLoginCbRef.current({ access_token: tokenResponse.access_token });
          } else {
            setGoogleLoading(false);
          }
        },
      });
    };
    document.body.appendChild(script);

    return () => {
      googleTokenClientRef.current = null;
      const existingScript = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const triggerGoogleSignIn = () => {
    if (!googleTokenClientRef.current) {
      setError('Google Sign-In not loaded. Please refresh the page.');
      return;
    }
    setError('');
    setGoogleLoading(true);
    googleTokenClientRef.current.requestAccessToken();
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-left">
          <Link to="/" className="auth-logo">
            <img src="/logo.png" alt="Milan AI" className="logo-image" />
            <span className="logo-text">Milan AI</span>
          </Link>
          
          <div className="auth-content">
            <h1>Welcome Back</h1>
            <p>Sign in to continue to your dashboard</p>

            {/* Login Type Toggle */}
            <div className="login-type-toggle">
              <button
                type="button"
                className={`toggle-btn ${loginType === 'brand' ? 'active' : ''}`}
                onClick={() => handleTypeChange('brand')}
              >
                <span className="toggle-icon">🏢</span>
                <span>Brand</span>
              </button>
              <button
                type="button"
                className={`toggle-btn ${loginType === 'creator' ? 'active' : ''}`}
                onClick={() => handleTypeChange('creator')}
              >
                <span className="toggle-icon">🎨</span>
                <span>Creator</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {sessionExpired && !error && (
                <div
                  className="info-message"
                  style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #fbbf24',
                    marginBottom: '16px',
                    fontSize: '14px',
                  }}
                >
                  Your session has expired. Please log in again to continue.
                </div>
              )}
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group" onClick={() => emailRef.current?.focus()}>
                <label htmlFor="email" style={{ color: '#000000', fontWeight: 600 }}>Email Address</label>
                <input
                  ref={emailRef}
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={(e) => e.target.select()}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  autoFocus
                  style={{ color: '#000000' }}
                />
              </div>

              <div className="form-group" onClick={() => passwordRef.current?.focus()}>
                <label htmlFor="password" style={{ color: '#000000', fontWeight: 600 }}>Password</label>
                <input
                  ref={passwordRef}
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={(e) => e.target.select()}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  style={{ color: '#000000' }}
                />
              </div>

              <div className="form-options">
                <label className="remember-me">
                  <input 
                    type="checkbox" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <a href={`/forgot-password?type=${loginType}`} className="forgot-password">Forgot password?</a>
              </div>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-divider">
              <span>or continue with</span>
            </div>

            <div className="social-auth">
              <button 
                className="social-btn google"
                type="button"
                onClick={triggerGoogleSignIn}
                disabled={googleLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {googleLoading ? 'Connecting...' : 'Google'}
              </button>
            </div>

            <p className="auth-footer">
              Don't have an account? <Link to="/signup">Sign up for free</Link>
            </p>
          </div>
        </div>

        <div className={`auth-right ${loginType}`}>
          {loginType === 'brand' ? (
            <>
              <div className="auth-illustration">
                <div className="stats-card">
                  <span className="stats-number">AI</span>
                  <span className="stats-label">Creator Matching</span>
                </div>
                <div className="stats-card">
                  <span className="stats-number">Secure</span>
                  <span className="stats-label">Escrow Payments</span>
                </div>
                <div className="stats-card">
                  <span className="stats-number">3.5%</span>
                  <span className="stats-label">Success Fee Only</span>
                </div>
              </div>
              <h2>Connect with Instagram Creators</h2>
              <p>Find creators, run campaigns, and track performance — all in one place</p>
            </>
          ) : (
            <>
              <div className="auth-illustration creator-illustration">
                <div className="stats-card">
                  <span className="stats-number">Secure</span>
                  <span className="stats-label">Milestone Payments</span>
                </div>
                <div className="stats-card">
                  <span className="stats-number">Simple</span>
                  <span className="stats-label">Content Workflow</span>
                </div>
                <div className="stats-card">
                  <span className="stats-number">Real</span>
                  <span className="stats-label">Performance Tracking</span>
                </div>
              </div>
              <h2>Grow Your Creator Career</h2>
              <p>Get discovered by brands and work on campaigns that match your style</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
